import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Paper, 
  Divider,
  Avatar,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Timer as TimerIcon,
  SportsTennis as MatchIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const MatchStub = ({ match, position, onClick }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = () => {
    setOpen(true);
    if (onClick) {
      onClick(match);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleViewMatch = () => {
    // TODO: Navigate to match details page
    console.log(`Navigate to /matches/${match.id}`);
    setOpen(false);
  };

  const handleCreateResult = () => {
    // TODO: Navigate to new match page with prefilled data
    console.log('Navigate to /new-match with prefilled tournament match data');
    setOpen(false);
  };

  const getStatusLabel = () => {
    if (match.winner) {
      return 'Finalizado';
    } else if (match.player1 && match.player2) {
      return 'Pendiente';
    } else {
      return 'Por definir';
    }
  };

  const getStatusColor = () => {
    if (match.winner) {
      return 'success';
    } else if (match.player1 && match.player2) {
      return 'warning';
    } else {
      return 'default';
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        onClick={handleOpen}
        sx={{
          p: 1.2,
          minWidth: isMobile ? 160 : 180,
          maxWidth: isMobile ? 180 : 220,
          backgroundColor: match.winner ? 'rgba(200, 158, 56, 0.05)' : 'background.paper',
          border: '1px solid',
          borderColor: match.winner ? 'rgba(200, 158, 56, 0.5)' : 'divider',
          borderRadius: 1.5,
          position: 'relative',
          zIndex: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            borderColor: '#c89e38'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          <Box sx={{ 
            p: 0.8, 
            bgcolor: match.winner === match.player1 ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
            borderRadius: 0.8,
            borderLeft: match.winner === match.player1 ? '3px solid #4caf50' : 'none',
            pl: match.winner === match.player1 ? 1 : 0.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography 
              variant="body2" 
              color={match.winner === match.player1 ? 'text.primary' : 'text.secondary'}
              sx={{ 
                fontWeight: match.winner === match.player1 ? 'bold' : 'normal',
                fontSize: isMobile ? '0.75rem' : '0.85rem'
              }}
            >
              {match.player1 || 'TBD'}
            </Typography>
            {match.winner === match.player1 && (
              <TrophyIcon sx={{ color: '#c89e38', fontSize: '1rem' }} />
            )}
          </Box>
          <Divider sx={{ my: 0.2 }} />
          <Box sx={{ 
            p: 0.8, 
            bgcolor: match.winner === match.player2 ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
            borderRadius: 0.8,
            borderLeft: match.winner === match.player2 ? '3px solid #4caf50' : 'none',
            pl: match.winner === match.player2 ? 1 : 0.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography 
              variant="body2" 
              color={match.winner === match.player2 ? 'text.primary' : 'text.secondary'}
              sx={{ 
                fontWeight: match.winner === match.player2 ? 'bold' : 'normal',
                fontSize: isMobile ? '0.75rem' : '0.85rem'
              }}
            >
              {match.player2 || 'TBD'}
            </Typography>
            {match.winner === match.player2 && (
              <TrophyIcon sx={{ color: '#c89e38', fontSize: '1rem' }} />
            )}
          </Box>
        </Box>
        
        {/* Status indicator */}
        <Box sx={{
          position: 'absolute',
          bottom: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: match.winner ? '#4caf50' : match.player1 && match.player2 ? '#ff9800' : '#bdbdbd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid white'
        }}>
          {match.winner ? (
            <TrophyIcon sx={{ color: 'white', fontSize: '0.75rem' }} />
          ) : (
            <TimerIcon sx={{ color: 'white', fontSize: '0.75rem' }} />
          )}
        </Box>
      </Paper>

      {/* Match Detail Modal */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="match-details-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle 
          component="div"
          id="match-details-title" 
          sx={{ 
            pb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            sx={{ 
              color: '#c89e38', 
              fontSize: '1.1rem' 
            }}
          >
            Detalles de la Partida
          </Typography>
          <IconButton 
            onClick={handleClose}
            size="small"
            aria-label="cerrar"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Chip 
              icon={match.winner ? <TrophyIcon /> : <TimerIcon />}
              label={getStatusLabel()}
              color={getStatusColor()}
              size="small"
              sx={{ 
                mb: 2,
                height: 24,
                '& .MuiChip-label': {
                  fontSize: '0.75rem'
                }
              }}
            />
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1.5,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}>
              {/* Player 1 */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: match.winner === match.player1 ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                borderRadius: 1,
                borderLeft: match.winner === match.player1 ? '3px solid #4caf50' : 'none',
                pl: match.winner === match.player1 ? 1.8 : 1.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar 
                    src={`https://mui.com/static/images/avatar/${Math.floor(Math.random() * 8) + 1}.jpg`}
                    alt={match.player1 || 'TBD'}
                    sx={{
                      width: 40,
                      height: 40,
                      border: match.winner === match.player1 ? '2px solid #4caf50' : 'none'
                    }}
                  />
                  <Typography 
                    variant="body1" 
                    fontWeight={match.winner === match.player1 ? 'bold' : 'normal'}
                    color={match.winner === match.player1 ? 'text.primary' : 'text.secondary'}
                  >
                    {match.player1 || 'Por determinar'}
                  </Typography>
                </Box>
                {match.winner === match.player1 && <TrophyIcon sx={{ color: '#c89e38' }} />}
              </Box>
              
              {/* VS */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 0.5
              }}>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                  VS
                </Typography>
              </Box>
              
              {/* Player 2 */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: match.winner === match.player2 ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                borderRadius: 1,
                borderLeft: match.winner === match.player2 ? '3px solid #4caf50' : 'none',
                pl: match.winner === match.player2 ? 1.8 : 1.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar 
                    src={`https://mui.com/static/images/avatar/${Math.floor(Math.random() * 8) + 1}.jpg`}
                    alt={match.player2 || 'TBD'}
                    sx={{
                      width: 40,
                      height: 40,
                      border: match.winner === match.player2 ? '2px solid #4caf50' : 'none'
                    }}
                  />
                  <Typography 
                    variant="body1" 
                    fontWeight={match.winner === match.player2 ? 'bold' : 'normal'}
                    color={match.winner === match.player2 ? 'text.primary' : 'text.secondary'}
                  >
                    {match.player2 || 'Por determinar'}
                  </Typography>
                </Box>
                {match.winner === match.player2 && <TrophyIcon sx={{ color: '#c89e38' }} />}
              </Box>
            </Box>
          </Box>
          
          {match.round && (
            <Box 
              sx={{ 
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {match.bracket === 'winners' ? 'Bracket de Ganadores' : 
                 match.bracket === 'losers' ? 'Bracket de Perdedores' : 
                 match.bracket === 'grand_final' ? 'Gran Final' : ''} - 
                Ronda {match.round}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          {match.winner ? (
            <Button 
              onClick={handleViewMatch}
              startIcon={<MatchIcon />}
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main'
                }
              }}
            >
              Ver Detalles de la Partida
            </Button>
          ) : (
            match.player1 && match.player2 ? (
              <Button 
                onClick={handleCreateResult}
                startIcon={<TrophyIcon />}
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  bgcolor: '#c89e38',
                  '&:hover': {
                    bgcolor: '#b38d2d'
                  }
                }}
              >
                Cargar Resultado
              </Button>
            ) : (
              <Button 
                disabled
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none'
                }}
              >
                Partida aún no definida
              </Button>
            )
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MatchStub; 